import {Component, OnInit, TemplateRef} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DashboardService} from '../dashboard.service';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from '../../core/http/api.service';
import {UtilsService} from '../../core/services/utils/utils.service';
import {Category} from '../../core/models/category';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {createCroppedImageFile, getUploadProgress, isImageFile} from '../../core/services/utils/image-upload.utils';

@Component({
    selector: 'app-edit-category',
    templateUrl: './edit-category.component.html',
    styleUrls: ['./edit-category.component.css'],
    standalone: false
})
export class EditCategoryComponent implements OnInit {

  isInfoParamsAdded = false;
  isSubParamsAdded = false;
  savingCategory = false;
  savingInfo = false;
  savingSub = false;
  categoryId = '';
  category: any = {
    categoryName: '',
    className: '',
    icon: '',
    status: 'Enabled',
    isFeatured: false,
    allowedUserActions: {
      bhakt: true,
      unbhakt: true,
      rating: true,
      story: true,
      hypocrisy: true
    },
    forceEntityToUseCategoryAllowedUserActions: false
  };

  // subFields: any[] = [];
  subParam: any = {id: '', fieldName: ''};

  // INFO PARAMS
  infoParamData = {informative_fields: []};
  addInfoItemData = {id: '', fieldName: '', fieldType: 'text', isRequired: false, lookUpRecordId: ''};

  // SUB PARAMS
  subParamData = {subjective_fields: []};
  addSubItemData = {id: '', fieldName: ''};
  modalRef: BsModalRef;
  croppedFile: any;
  imageChangedEvent: any = '';
  aspectRatio = 1;
  uploadingIcon = false;
  uploadProgress = 0;

  categories: Category[] = [];

  constructor(
    private route: ActivatedRoute,
    private dashApi: DashboardService,
    private api: ApiService,
    private utils: UtilsService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private httpClient: HttpClient) {
    this.route.params.subscribe(params => this.categoryId = params.id);
  }

  ngOnInit(): void {
    this.getCurrentCategory();
    this.getInfoParams();
    this.getSubParams();
    this.getCategories();
  }

  get hasCategoryId(): boolean {
    return !!this.categoryId;
  }

  private extractArray(data: any): any[] {
    const buckets = [data, data?.response, data?.data];
    for (const bucket of buckets) {
      if (Array.isArray(bucket)) {
        return bucket;
      }
      if (!bucket || typeof bucket !== 'object') {
        continue;
      }
      if (Array.isArray(bucket?.categories)) {
        return bucket.categories;
      }
      if (Array.isArray(bucket?.informative_fields)) {
        return bucket.informative_fields;
      }
      if (Array.isArray(bucket?.subjective_fields)) {
        return bucket.subjective_fields;
      }
      if (Array.isArray(bucket?.items)) {
        return bucket.items;
      }
    }
    return [];
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true';
  }

  private normalizeCategory(data: any): any {
    const payload = data?.response || data?.data || data;
    return {
      ...this.category,
      ...payload,
      isFeatured: this.toBoolean(payload?.isFeatured),
      forceEntityToUseCategoryAllowedUserActions: this.toBoolean(payload?.forceEntityToUseCategoryAllowedUserActions),
      allowedUserActions: {
        bhakt: true,
        unbhakt: true,
        rating: true,
        story: true,
        hypocrisy: true,
        ...(payload?.allowedUserActions || {})
      }
    };
  }

  goBack(): void {
    this.utils.goBack();
  }

  getCurrentCategory(): void {
    this.dashApi.getSingleCategory(this.categoryId).subscribe((data) => {
      this.category = this.normalizeCategory(data);
    }, err => this.utils.resErr(err));
  }

  updateCategory(): void {
    if (!this.hasCategoryId) {
      this.utils.toastError('Invalid category');
      return;
    }
    this.savingCategory = true;
    this.dashApi.updateCategory(this.category, this.categoryId).subscribe(() => {
      this.savingCategory = false;
      this.utils.toastSuccess('Category Updated Successfully!');
    }, err => {
      this.savingCategory = false;
      this.utils.resErr(err);
    });
  }

  fileChangeEvent(event: any, template: TemplateRef<any>): void {
    const input = event?.target as HTMLInputElement;
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    if (!isImageFile(file)) {
      this.utils.toastError('Please select an image file only');
      input.value = '';
      return;
    }
    this.modalRef = this.modalService.show(template);
    this.aspectRatio = 1;
    this.croppedFile = null;
    this.imageChangedEvent = event;
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedFile = createCroppedImageFile(event);
  }

  loadImageFailed(): void {
    this.utils.toastError('Image Load failed!');
  }

  closeCropperModal(): void {
    if (this.uploadingIcon) {
      this.utils.toastError('Please wait for upload to complete');
      return;
    }
    if (this.modalRef) {
      this.modalRef.hide();
    }
    if (this.imageChangedEvent?.target) {
      this.imageChangedEvent.target.value = '';
    }
    this.imageChangedEvent = null;
    this.croppedFile = null;
    this.uploadingIcon = false;
    this.uploadProgress = 0;
  }

  uploadImage(): void {
    if (!this.croppedFile) {
      this.utils.toastError('Please crop the selected image');
      return;
    }
    this.uploadingIcon = true;
    const formData = new FormData();
    formData.append('file', this.croppedFile, this.croppedFile?.name || `upload-${Date.now()}.png`);
    const upload = this.httpClient.post<any>('file-upload', formData, {
      reportProgress: true,
      observe: 'events'
    });
    upload.subscribe(
      (res: any) => {
        if (res.type === HttpEventType.UploadProgress) {
          this.uploadProgress = getUploadProgress(res.loaded, res.total);
        } else if (res.type === HttpEventType.Response) {
          this.uploadingIcon = false;
          this.category.icon = res.body.response.url;
          this.uploadProgress = 0;
          this.closeCropperModal();
        }
      },
      (err) => {
        this.uploadingIcon = false;
        this.utils.resErr(err);
      }
    );
  }

  updateInfoParams(): void {
    if (!this.hasCategoryId) {
      this.utils.toastError('Invalid category');
      return;
    }
    this.savingInfo = true;
    this.dashApi.updateInformativeParam(this.infoParamData, this.categoryId).subscribe(res => {
      this.toastr.success('Informative Parameters updated');
      this.isInfoParamsAdded = true;
      this.savingInfo = false;
    }, err => {
      this.savingInfo = false;
      this.toastr.error(err.error?.message || 'Unable to update informative parameters');
    });
  }

  // INFO PARAMS

  deleteInfoItem(index: number): void {
    if (confirm('Are you sure you want to delete this informative parameter ?')) {
      const fieldId = this.infoParamData.informative_fields[index].id;
      if (fieldId) {
        this.api.deleteInformativeParameter(fieldId).subscribe(res => this.toastr.success(res.message));
        this.infoParamData.informative_fields.splice(index, 1);
      } else {
        this.infoParamData.informative_fields.splice(index, 1);
      }
    }
  }

  addInfoItem(): void {
    this.infoParamData.informative_fields.push(this.addInfoItemData);
    this.addInfoItemData = {
      id: '',
      fieldName: '',
      fieldType: 'text',
      isRequired: false,
      lookUpRecordId: ''
    };
  }

  getInfoParams(): void {
    this.dashApi.getInfoParamsByCategory(this.categoryId).subscribe(res => {
      const fields = this.extractArray(res);
      this.infoParamData.informative_fields = fields.map((item: any) => {
        const normalized = {...item};
        delete normalized.createdAt;
        delete normalized.updatedAt;
        delete normalized.category;
        return normalized;
      });
    }, err => this.utils.resErr(err));
  }

  // SUB PARAMS
  getSubParams(): void {
    this.dashApi.getSubParamsByCategory(this.categoryId).subscribe((res) => {
      const fields = this.extractArray(res);
      this.subParamData.subjective_fields = fields.map((item: any) => {
        const normalized = {...item};
        delete normalized.createdAt;
        delete normalized.updatedAt;
        delete normalized.category;
        return normalized;
      });
    }, err => this.utils.resErr(err));
  }

  addSubItem(): void {
    this.subParamData.subjective_fields.push(this.addSubItemData);
    this.addSubItemData = {id: '', fieldName: ''};
  }

  deleteSubItem(index: number): void {
    if (confirm('Are you sure you want to delete this subjective parameter ?')) {
      const fieldId = this.subParamData.subjective_fields[index].id;
      if (fieldId) {
        this.api.deleteSubjectiveParameter(fieldId).subscribe(res => this.toastr.success(res.message));
        this.subParamData.subjective_fields.splice(index, 1);
      } else {
        this.subParamData.subjective_fields.splice(index, 1);
      }
    }
  }

  updateSubParams(): void {
    if (!this.hasCategoryId) {
      this.utils.toastError('Invalid category');
      return;
    }
    this.savingSub = true;
    this.dashApi.updateSubjectiveParam(this.subParamData, this.categoryId).subscribe(res => {
      this.toastr.success('Subjective Parameters updated');
      this.isSubParamsAdded = true;
      this.savingSub = false;
    }, err => {
      this.savingSub = false;
      this.toastr.error(err.error?.message || 'Unable to update subjective parameters');
    });
  }

  getCategories(): void {
    this.dashApi.getAllCategories('').subscribe(res => {
      const list = this.extractArray(res);
      this.categories = list as Category[];
      if (this.categories.length) {
        this.addInfoItemData.lookUpRecordId = this.categories[0].id;
      }
    }, err => this.utils.resErr(err));
  }
}
